import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Send, 
  MessageCircle,
  Mic,
  MicOff,
  Image as ImageIcon,
  Loader2,
  Pin,
  Trash2,
  VolumeX,
  MoreVertical,
  Smile,
  ShoppingBag,
  Crown,
  Shield,
  Users,
  Play,
  Square
} from "lucide-react";

const ClubChatPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [muteExpiresAt, setMuteExpiresAt] = useState(null);
  const [ownedStickers, setOwnedStickers] = useState([]);
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const messagesEndRef = useRef(null);
  const refreshInterval = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);

  const clubId = user?.clubId;
  const isAdmin = ["president", "manager", "captain"].includes(user?.clubRole);

  useEffect(() => {
    if (!clubId) {
      toast.error("Bir kulübe üye olmalısınız");
      navigate("/clubs");
      return;
    }

    fetchMessages();
    fetchOwnedStickers();
    
    // Her 3 saniyede mesajları yenile
    refreshInterval.current = setInterval(fetchMessages, 3000);
    
    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
    };
  }, [clubId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/club/${clubId}/chat`);
      setMessages(response.data.messages || []);
      setPinnedMessages(response.data.pinnedMessages || []);
      setIsMuted(response.data.isMuted || false);
      setMuteExpiresAt(response.data.muteExpiresAt);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnedStickers = async () => {
    try {
      const response = await axios.get(`${API}/stickers/owned`);
      setOwnedStickers(response.data.stickers || []);
    } catch (error) {
      console.error("Error fetching stickers:", error);
    }
  };

  const handleSendMessage = async (stickerId = null, stickerEmoji = null) => {
    if (isMuted) {
      toast.error("Yazma yetkiniz kısıtlanmış");
      return;
    }
    if (!newMessage.trim() && !stickerId && !audioBlob) return;
    
    setSending(true);
    try {
      let voiceUrl = null;
      
      // Sesli mesaj yükleme (gerçek uygulamada bir storage'a yüklenir)
      if (audioBlob) {
        // Burada normalde bir storage'a yüklenip URL alınır
        // Şimdilik base64 olarak gönderelim (demo amaçlı)
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        voiceUrl = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
        });
      }

      const params = new URLSearchParams();
      if (newMessage.trim()) params.append('content', newMessage);
      if (stickerId) params.append('stickerId', stickerId);
      if (voiceUrl) {
        params.append('voiceUrl', voiceUrl);
        params.append('voiceDuration', recordingTime);
      }

      const response = await axios.post(`${API}/club/${clubId}/chat/send?${params.toString()}`);
      
      if (response.data.success) {
        setMessages(prev => [...prev, response.data.message]);
        setNewMessage("");
        setShowStickerPicker(false);
        setAudioBlob(null);
        setRecordingTime(0);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Mesaj gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  const handlePinMessage = async (messageId) => {
    try {
      await axios.post(`${API}/club/${clubId}/chat/${messageId}/pin`);
      toast.success("Mesaj sabitlendi");
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/club/${clubId}/chat/${messageId}`);
      toast.success("Mesaj silindi");
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleMuteUser = async (targetUserId, hours = null) => {
    try {
      const params = new URLSearchParams();
      if (hours) params.append('duration_hours', hours);
      
      await axios.post(`${API}/club/${clubId}/chat/mute/${targetUserId}?${params.toString()}`);
      toast.success(hours ? `Kullanıcı ${hours} saat susturuldu` : "Kullanıcı susturuldu");
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      toast.error("Mikrofon erişimi sağlanamadı");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setAudioBlob(null);
      setRecordingTime(0);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'president':
        return <Badge className="bg-amber-500 text-white text-xs"><Crown className="h-3 w-3 mr-1" />Başkan</Badge>;
      case 'manager':
        return <Badge className="bg-blue-500 text-white text-xs"><Shield className="h-3 w-3 mr-1" />Yönetici</Badge>;
      case 'captain':
        return <Badge className="bg-emerald-500 text-white text-xs">Kaptan</Badge>;
      default:
        return null;
    }
  };

  if (!clubId) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <Card className="h-[calc(100vh-150px)] flex flex-col">
          {/* Başlık */}
          <CardHeader className="border-b py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="h-5 w-5 text-emerald-600" />
                    {user?.clubName || "Kulüp"} Sohbet
                  </CardTitle>
                  {isMuted && (
                    <p className="text-xs text-red-500">
                      Yazma yetkiniz kısıtlanmış {muteExpiresAt && `(${new Date(muteExpiresAt).toLocaleString('tr-TR')} kadar)`}
                    </p>
                  )}
                </div>
              </div>
              {isAdmin && (
                <Badge variant="outline" className="text-emerald-600">
                  Yönetici
                </Badge>
              )}
            </div>
          </CardHeader>

          {/* Sabitlenmiş Mesajlar */}
          {pinnedMessages.length > 0 && (
            <div className="bg-amber-50 border-b px-4 py-2">
              <div className="flex items-center gap-2 text-amber-700">
                <Pin className="h-4 w-4" />
                <span className="text-sm font-medium">Sabitlenmiş:</span>
                <span className="text-sm truncate">{pinnedMessages[0]?.content || "📌 Sticker"}</span>
              </div>
            </div>
          )}

          {/* Mesajlar */}
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full p-4">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <MessageCircle className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Henüz mesaj yok</p>
                    <p className="text-sm text-gray-400">İlk mesajı siz gönderin!</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, idx) => {
                    const isOwn = msg.senderId === user?.id;
                    return (
                      <div
                        key={msg.id || idx}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`flex gap-2 max-w-[80%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                          {!isOwn && (
                            <Avatar className="h-8 w-8 shrink-0">
                              <AvatarImage src={msg.senderImage} />
                              <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">
                                {msg.senderName?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                          )}
                          <div>
                            {!isOwn && (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-gray-700">{msg.senderName}</span>
                                {getRoleBadge(msg.senderRole)}
                              </div>
                            )}
                            <div className="flex items-end gap-1 group">
                              <div
                                className={`rounded-2xl px-4 py-2 ${
                                  isOwn
                                    ? 'bg-emerald-500 text-white rounded-tr-sm'
                                    : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                                } ${msg.isPinned ? 'ring-2 ring-amber-400' : ''}`}
                              >
                                {msg.stickerEmoji ? (
                                  <span className="text-4xl">{msg.stickerEmoji}</span>
                                ) : msg.voiceUrl ? (
                                  <div className="flex items-center gap-2">
                                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                                      <Play className="h-4 w-4" />
                                    </Button>
                                    <span className="text-sm">{formatDuration(msg.voiceDuration || 0)}</span>
                                  </div>
                                ) : (
                                  <p>{msg.content}</p>
                                )}
                              </div>
                              
                              {/* Mesaj Aksiyonları */}
                              {(isOwn || isAdmin) && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <MoreVertical className="h-3 w-3" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align={isOwn ? "end" : "start"}>
                                    {isAdmin && !msg.isPinned && (
                                      <DropdownMenuItem onClick={() => handlePinMessage(msg.id)}>
                                        <Pin className="h-4 w-4 mr-2" />
                                        Sabitle
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem 
                                      onClick={() => handleDeleteMessage(msg.id)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Sil
                                    </DropdownMenuItem>
                                    {isAdmin && !isOwn && (
                                      <>
                                        <DropdownMenuItem onClick={() => handleMuteUser(msg.senderId, 1)}>
                                          <VolumeX className="h-4 w-4 mr-2" />
                                          1 saat sustur
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleMuteUser(msg.senderId, 24)}>
                                          <VolumeX className="h-4 w-4 mr-2" />
                                          24 saat sustur
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                            <span className={`text-xs text-gray-400 ${isOwn ? 'text-right block' : ''}`}>
                              {formatTime(msg.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
          </CardContent>

          {/* Mesaj Gönderme */}
          <div className="border-t p-3">
            {audioBlob ? (
              <div className="flex items-center gap-3 bg-emerald-50 rounded-lg p-3">
                <div className="flex-1 flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium">{formatDuration(recordingTime)}</span>
                  <span className="text-sm text-gray-500">Sesli mesaj hazır</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setAudioBlob(null)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
                <Button 
                  onClick={() => handleSendMessage()}
                  disabled={sending}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            ) : isRecording ? (
              <div className="flex items-center gap-3 bg-red-50 rounded-lg p-3">
                <div className="flex-1 flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-red-600">Kayıt yapılıyor... {formatDuration(recordingTime)}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={cancelRecording}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
                <Button onClick={stopRecording} className="bg-red-600 hover:bg-red-700">
                  <Square className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {/* Sticker Picker */}
                <Popover open={showStickerPicker} onOpenChange={setShowStickerPicker}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="shrink-0" disabled={isMuted}>
                      <Smile className="h-5 w-5 text-gray-500" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="p-3 border-b">
                      <p className="font-medium text-sm">Stickerlar</p>
                    </div>
                    <ScrollArea className="h-48">
                      {ownedStickers.length === 0 ? (
                        <div className="p-4 text-center">
                          <p className="text-sm text-gray-500">Henüz sticker paketiniz yok</p>
                          <Button size="sm" className="mt-2" onClick={() => navigate('/messages')}>
                            Mağazaya Git
                          </Button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-5 gap-2 p-3">
                          {ownedStickers.map((sticker) => (
                            <button
                              key={sticker.id}
                              onClick={() => handleSendMessage(sticker.id, sticker.emoji)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-2xl"
                            >
                              {sticker.emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </PopoverContent>
                </Popover>

                {/* Sesli Mesaj */}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="shrink-0"
                  onClick={startRecording}
                  disabled={isMuted}
                >
                  <Mic className="h-5 w-5 text-gray-500" />
                </Button>

                <Input
                  placeholder={isMuted ? "Yazma yetkiniz kısıtlanmış" : "Mesajınızı yazın..."}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  className="flex-1"
                  disabled={isMuted}
                />
                <Button 
                  onClick={() => handleSendMessage()}
                  disabled={sending || !newMessage.trim() || isMuted}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ClubChatPage;
